import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useDeviceOS from "@/lib/useDeviceOS";

/**
 * Price — Page /download
 * Détecte iOS/Android via UserAgent et met en avant le bon store.
 * Google Play est grisé "Bientôt disponible" tant que la version Android
 * n'est pas publiée. iOS pointe sur l'App Store France.
 */

const APP_STORE_URL = "https://apps.apple.com/fr/app/price-covoit/id6793724470";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.price.covoiturage";

function AppleIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.6 13.05c0-2.55 2.1-3.78 2.19-3.84a4.7 4.7 0 0 0-3.72-2.01c-1.57-.16-3.07.93-3.87.93-.8 0-2.02-.91-3.32-.88-1.71.02-3.29 1-4.16 2.53-1.79 3.1-.46 7.68 1.26 10.19.84 1.24 1.83 2.62 3.13 2.57 1.27-.05 1.74-.81 3.27-.81 1.51 0 1.96.81 3.29.78 1.37-.02 2.22-1.25 3.05-2.5.96-1.43 1.35-2.83 1.37-2.91-.03-.01-2.63-1-2.66-3.99Zm-2.6-7.34c.7-.85 1.17-2.03 1.04-3.2-1.01.04-2.23.67-2.95 1.52-.65.75-1.22 1.95-1.07 3.1 1.13.09 2.28-.57 2.98-1.42Z" />
    </svg>
  );
}
function AndroidIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.6 9.48 19 7.05a.4.4 0 0 0-.7-.4l-1.42 2.47a10 10 0 0 0-9.76 0L5.7 6.65a.4.4 0 0 0-.7.4l1.4 2.43A9 9 0 0 0 2 16h20a9 9 0 0 0-4.4-6.52ZM8 14.5a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8Zm8 0a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Device OS detection : voir `/app/frontend/src/lib/useDeviceOS.js`
// ---------------------------------------------------------------------------

function StoreCard({ href, disabled, featured, icon, eyebrow, title, testId }) {
  const baseStyle = disabled
    ? { background: "rgba(255,255,255,0.6)", color: "rgba(30,9,16,0.4)", cursor: "not-allowed" }
    : featured
      ? { background: "#F86A78", color: "#fff", boxShadow: "8px 8px 0 0 #1E0910" }
      : { background: "#fff", color: "#1E0910", boxShadow: "5px 5px 0 0 #1E0910" };
  const borderClass = disabled ? "street-border-thin" : featured ? "street-border" : "street-border-thin";
  const inner = (
    <>
      <div
        className={`grid h-14 w-14 flex-none place-items-center rounded-2xl street-border-thin`}
        style={{
          background: disabled ? "rgba(30,9,16,0.05)" : featured ? "rgba(255,255,255,0.15)" : "#FFE9D6",
          color: disabled ? "rgba(30,9,16,0.3)" : featured ? "#fff" : "#1E0910",
        }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: disabled ? "inherit" : featured ? "rgba(255,255,255,0.7)" : "rgba(30,9,16,0.6)" }}>
          {eyebrow}
        </div>
        <div className="mt-0.5 font-[Outfit] text-2xl font-black leading-tight">{title}</div>
      </div>
      {!disabled && (
        <svg viewBox="0 0 24 24" className="h-6 w-6 flex-none" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      )}
      {disabled && (
        <div className="ml-auto rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest street-border-thin" style={{ background: "rgba(30,9,16,0.05)", color: "rgba(30,9,16,0.5)" }}>
          Bientôt
        </div>
      )}
    </>
  );
  const wrapClass = `street-press flex w-full items-center gap-5 rounded-[28px] p-5 transition-all ${borderClass}`;
  if (disabled) return <div data-testid={testId} aria-disabled="true" className={wrapClass} style={baseStyle} title="Bientôt disponible">{inner}</div>;
  return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={wrapClass} style={baseStyle}>{inner}</a>;
}

export default function PriceDownload() {
  const { os, hydrated } = useDeviceOS();
  const iosFeatured = !hydrated || os !== "android";

  useEffect(() => {
    document.title = "Télécharger Price · Price Covoiturage";
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Télécharge Price sur iPhone (App Store) et Android (Google Play). Le covoiturage instantané entre particuliers.");
  }, []);

  return (
    <main className="min-h-screen" style={{ background: "#FFF6EE" }}>
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(30,9,16,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(30,9,16,0.06) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        aria-hidden
      />
      <div className="relative mx-auto flex max-w-lg flex-col items-center px-5 py-14 sm:py-24">
        <Link to="/" className="mb-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest transition-colors hover:!text-[#F86A78]" style={{ color: "rgba(30,9,16,0.6)" }}>
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Retour à l&apos;accueil
        </Link>

        <div className="mt-6 grid h-20 w-20 place-items-center rounded-[24px] text-white street-border" style={{ background: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}>
          <span className="font-[Outfit] text-[38px] font-black leading-none">P</span>
        </div>

        <h1 className="mt-8 text-center font-[Outfit] text-5xl font-black leading-none tracking-tight sm:text-6xl" style={{ color: "#1E0910" }}>
          Télécharge <span style={{ color: "#F86A78" }}>Price</span>
        </h1>
        <p className="mt-4 max-w-sm text-center font-[Manrope] text-lg" style={{ color: "rgba(30,9,16,0.7)" }}>
          Choisis ton store — l&apos;installation prend moins d&apos;une minute.
        </p>

        {/* Réseaux sociaux — placés AU-DESSUS des stores pour maximiser
            le suivi avant le téléchargement. Même design/cohérence que
            le bloc social de /PriceHome.jsx. */}
        <div className="mt-10 w-full rounded-2xl bg-white p-4 street-border-thin" style={{ boxShadow: "3px 3px 0 0 #1E0910" }}>
          <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "rgba(30,9,16,0.6)" }}>
            Suis-nous sur les réseaux
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a
              href="https://www.instagram.com/pricecovoit"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-social-instagram"
              className="group flex items-center gap-2 rounded-xl px-3 py-2.5 street-border-thin active:translate-y-[1px] transition-transform"
              style={{
                background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)",
                boxShadow: "3px 3px 0 0 #1E0910",
              }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor" aria-hidden>
                <path d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.22.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.05.42 2.22.06 1.26.07 1.64.07 4.85s0 3.6-.07 4.85c-.05 1.17-.25 1.8-.42 2.22-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.05.37-2.22.42-1.26.06-1.64.07-4.85.07s-3.6 0-4.85-.07c-1.17-.05-1.8-.25-2.22-.42-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.17-.42-.37-1.05-.42-2.22C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.85c.05-1.17.25-1.8.42-2.22.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.05-.37 2.22-.42C8.4 2.2 8.8 2.2 12 2.2Zm0 1.98c-3.15 0-3.52 0-4.76.07-1.07.05-1.65.23-2.04.38-.51.2-.87.43-1.26.82-.39.39-.62.75-.82 1.26-.15.39-.33.97-.38 2.04C2.65 8.48 2.65 8.85 2.65 12s0 3.52.07 4.76c.05 1.07.23 1.65.38 2.04.2.51.43.87.82 1.26.39.39.75.62 1.26.82.39.15.97.33 2.04.38 1.24.07 1.61.07 4.76.07s3.52 0 4.76-.07c1.07-.05 1.65-.23 2.04-.38.51-.2.87-.43 1.26-.82.39-.39.62-.75.82-1.26.15-.39.33-.97.38-2.04.07-1.24.07-1.61.07-4.76s0-3.52-.07-4.76c-.05-1.07-.23-1.65-.38-2.04-.2-.51-.43-.87-.82-1.26-.39-.39-.75-.62-1.26-.82-.39-.15-.97-.33-2.04-.38-1.24-.07-1.61-.07-4.76-.07Zm0 3.4a4.42 4.42 0 1 1 0 8.84 4.42 4.42 0 0 1 0-8.84Zm0 1.98a2.44 2.44 0 1 0 0 4.88 2.44 2.44 0 0 0 0-4.88Zm4.65-2.14a1.03 1.03 0 1 1 0 2.06 1.03 1.03 0 0 1 0-2.06Z" />
              </svg>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-black uppercase tracking-widest text-white/80">Instagram</div>
                <div className="font-[Outfit] text-[13px] font-black text-white leading-none truncate">@pricecovoit</div>
              </div>
            </a>
            <a
              href="https://www.tiktok.com/@price.covoit"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-social-tiktok"
              className="group flex items-center gap-2 rounded-xl px-3 py-2.5 street-border-thin active:translate-y-[1px] transition-transform"
              style={{
                background: "#000",
                boxShadow: "3px 3px 0 0 #1E0910",
              }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <path fill="#25F4EE" d="M19.6 6.9a5.6 5.6 0 0 1-4-1.5V15a5.7 5.7 0 1 1-5.6-5.7v3.2a2.5 2.5 0 1 0 2.4 2.5V2h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
                <path fill="#FE2C55" d="M21 8.4a5.6 5.6 0 0 1-4-1.5V16.5a5.7 5.7 0 1 1-5.6-5.7v3.2a2.5 2.5 0 1 0 2.4 2.5V3.5h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
                <path fill="#fff" d="M20.3 7.6a5.6 5.6 0 0 1-4-1.5v9.7A5.7 5.7 0 1 1 10.7 10v3.2a2.5 2.5 0 1 0 2.4 2.5V2.7h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
              </svg>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-black uppercase tracking-widest text-white/60">TikTok</div>
                <div className="font-[Outfit] text-[13px] font-black text-white leading-none truncate">@price.covoit</div>
              </div>
            </a>
          </div>
        </div>

        <div className="mt-5 grid w-full gap-4">
          <StoreCard featured={iosFeatured} href={APP_STORE_URL} icon={<AppleIcon className="h-7 w-7" />} eyebrow="Télécharger sur" title="App Store" testId="download-ios" />
          <StoreCard featured={hydrated && os === "android"} href={PLAY_STORE_URL} icon={<AndroidIcon className="h-7 w-7" />} eyebrow="Télécharger sur" title="Google Play" testId="download-android" />
        </div>

        {/* Bouton "Ouvrir la version web" — accessible sous les stores. */}
        <div className="mt-6 flex w-full justify-center">
          <a
            href="/welcome"
            data-testid="download-android-web-cta"
            className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-[Outfit] font-black text-white text-[15px] uppercase tracking-wider street-border active:translate-y-[2px] transition-all"
            style={{ background: "#F86A78", boxShadow: "5px 5px 0 0 #1E0910" }}
          >
            Ouvrir la version web
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="mt-10 flex items-center gap-6 text-xs" style={{ color: "rgba(30,9,16,0.6)" }}>
          <div className="flex items-center gap-2">
            <span style={{ color: "#F86A78" }}>★★★★★</span>
            <span className="font-semibold">4,8 · App Store</span>
          </div>
          <div className="flex items-center gap-2">
            <span>🔒</span>
            <span className="font-semibold">Paiement sécurisé</span>
          </div>
        </div>
      </div>
    </main>
  );
}
